import { DictionaryPhrases } from '@sitecore-jss/sitecore-jss-react';
import {
  getLoggedInUserEmail,
  getLoggedInUserName,
  isLoggedIn,
  getSubscriptionModeCookie,
  isSubscribedUserCookie,
} from '../utils/authHelper';
import { ICP_IDENTIFIER, STANDARDS_IDENTIFIER, TRAINING_IDENTIFIER } from '../utils/constants';

export const LOB_LIST = [STANDARDS_IDENTIFIER, ICP_IDENTIFIER, TRAINING_IDENTIFIER] as const;

export const INVOICE_LIST = [ICP_IDENTIFIER, 'CustomInvoice'];

const getInitialLobCounts = () => Object.fromEntries(LOB_LIST.map((lob) => [lob, 0]));

const getInitialOrderDetails = () => {
  return Object.fromEntries(LOB_LIST.map((lob) => [lob, { paymentType: 'None', orderIds: [] }]));
};

const getInitialInvoiceQuantity = () => {
  return Object.fromEntries(INVOICE_LIST.map((invoiceIdentifier) => [invoiceIdentifier, 0]));
};

const getInitialInvoiceSummary = () => {
  return Object.fromEntries(
    INVOICE_LIST.map((invoiceIdentifier) => [
      invoiceIdentifier,
      {
        totalAmount: 0,

        invoiceAmounts: {},
      },
    ])
  );
};

const getInitialIdentifier = () => {
  return Object.fromEntries(INVOICE_LIST.map((invoiceIdentifier) => [invoiceIdentifier, '']));
};

export const initialState: ReducerPayload = {
  userName: getLoggedInUserName(),
  userEmail: getLoggedInUserEmail(),
  loggedIn: isLoggedIn(),
  cartCounts: {
    totalCount: 0,
    lobCounts: getInitialLobCounts(),
  },
  orderTotals: {
    totalAmount: 0,
    lobTotals: getInitialLobCounts(),
  },
  maxCartItemQuantity: 0,
  toggleCartQuantityLoad: false,
  toggleOrderSummaryLoad: false,
  toggleInvoiceSummaryLoad: false,
  searchString: '',
  deliveryStatus: '',
  paymentType: 'None',
  toggleCalculateOrder: false,
  toggleProcessOrderValidation: false,
  toggleSubmitPaymentValidation: false,
  lastOrderId: '',
  sitecoreDictionaryData: {},
  isSubscribedUser: isSubscribedUserCookie(),
  isSubscriptionRep: false,
  subscriptionMode: getSubscriptionModeCookie(),
  subscriptionStatus: '',
  hasStatisticsReport: false,
  hasEULA: false,
  isGroupPaymentSuccessful: false,
  groupNumber: '',
  syncFailedCheckoutLOBs: [],
  orderDetails: getInitialOrderDetails(),
  invoicesSummary: getInitialInvoiceSummary(),
  invoicePayment: getInitialIdentifier(),
  groupNumberInvoiceId: getInitialIdentifier(),
  groupInvoiceId: getInitialIdentifier(),
  adminGroupNumber: '',
  invoiceCounts: {
    totalCount: 0,
    lobCounts: getInitialInvoiceQuantity(),
  },
  syncFailedCartLOBs: [],
  groupAdminCountry: '',
  shipToChangeAllowed: true,
  hardCopyQuantity: null,
  hasInvalidInvoices: false,
  remainingPaymentAttempts: null,
  togglePaymentResponse: false,
};

export interface CartCounts {
  totalCount: number;
  lobCounts: { [key: string]: number };
}

export interface InvoiceCounts {
  totalCount: number;
  lobCounts: { [key: string]: number };
}

export interface OrderDetails {
  paymentType: string;
  orderIds: string[];
}

export interface OrderTotals {
  totalAmount: number;
  lobTotals: { [key: string]: number };
}

export interface InvoiceDetails {
  totalAmount: number;

  invoiceAmounts: { [invoiceId: string]: number };
}

export interface ReducerPayload {
  userName?: string;
  userEmail?: string;
  loggedIn?: boolean;
  cartCounts?: CartCounts;
  maxCartItemQuantity?: number;
  toggleCartQuantityLoad?: boolean;
  toggleOrderSummaryLoad?: boolean;
  toggleInvoiceSummaryLoad?: boolean;
  description?: string;
  searchString?: string;
  deliveryStatus?: string;
  paymentType?: string;
  toggleCalculateOrder?: boolean;
  toggleProcessOrderValidation?: boolean;
  toggleSubmitPaymentValidation?: boolean;
  lastOrderId?: string;
  orderTotals?: OrderTotals;
  sitecoreDictionaryData?: DictionaryPhrases;
  isSubscribedUser?: boolean;
  isSubscriptionRep?: boolean;
  subscriptionMode?: string | null | undefined;
  subscriptionStatus?: string;
  hasStatisticsReport?: boolean;
  hasEULA?: boolean;
  isGroupPaymentSuccessful?: boolean;
  groupNumber?: string;
  orderDetails?: { [key: string]: OrderDetails };
  invoicesSummary?: { [invoiceIdentifier: string]: InvoiceDetails };
  invoicePayment?: { [invoiceIdentifier: string]: string };
  invoiceCounts?: InvoiceCounts;
  groupNumberInvoiceId?: { [invoiceIdentifier: string]: string };
  groupInvoiceId?: { [invoiceIdentifier: string]: string };
  adminGroupNumber?: string;
  syncFailedCartLOBs?: string[];
  syncFailedCheckoutLOBs?: string[];
  groupAdminCountry?: string;
  shipToChangeAllowed?: boolean;
  hardCopyQuantity?: Number | null;
  hasInvalidInvoices?: boolean;
  remainingPaymentAttempts?: number | null;
  togglePaymentResponse?: boolean;
}

export interface ContextReducerAction {
  type: string;
  payload: ReducerPayload;
}

export const contextReducer = (state = initialState, action: ContextReducerAction) => {
  switch (action.type) {
    case 'CART_UPDATE':
      return Object.assign({}, state, action.payload);
    case 'PRODUCT_UPDATE':
      return Object.assign({}, state, action.payload);
    case 'LOGIN_STATE':
      return Object.assign({}, state, action.payload);
    case 'SEARCH_UPDATE':
      return Object.assign({}, state, action.payload);
    case 'CHECKOUT_UPDATE':
      return Object.assign({}, state, action.payload);
    case 'SITECORE_DICTIONARY':
      return Object.assign({}, state, action.payload);
    case 'SUBSCRIPTION_STATE':
      return Object.assign({}, state, action.payload);
    case 'INVOICE_UPDATE':
      return Object.assign({}, state, action.payload);
    default:
      return state;
  }
};
